import { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import './Home.css';
import interiorPaint from '../../assets/interior-paint.jpeg';
import exteriorPaint from '../../assets/exterior-paint.jpeg';
import heroVideo from '../../assets/logo/paint_video.mp4'; 
import FeaturedProducts from './FeaturedProducts';
import Testimonials from './Testimonials';

const Home = () => {
  const navigate = useNavigate();
  
  const productsHeaderRef = useRef(null);
  const productCardsRef = useRef([]);
  const featureCardsRef = useRef([]);
  const videoRef = useRef(null);

  const categoryMapping = {
    'Interior wall paint': 1,
    'Exterior wall paint': 2, 
    'Waterproofing Services': 3,
    'Wood Solutions': 4,
    'Special Products': 5
  };

  const products = [
    {
      id: 1,
      name: 'Interior wall paint',
      description: 'Transform your indoor spaces with our premium interior paints',
      image: "https://plus.unsplash.com/premium_photo-1670360414483-64e6d9ba9038?q=80&w=870&auto=format&fit=crop&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D"
    },
    {
      id: 2,
      name: 'Exterior wall paint',
      description: 'Weather-resistant exterior paints for lasting protection',
      image: "https://images.unsplash.com/photo-1706808849780-7a04fbac83ef?q=80&w=1032&auto=format&fit=crop&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D"
    },
    {
      id: 3,
      name: 'Waterproofing Services',
      description: 'Advanced protective coatings for enhanced durability',
      image: interiorPaint
    },
    {
      id: 4,
      name: 'Wood Solutions',
      description: 'Add depth and character with our textured finishes',
      image: exteriorPaint
    },
  ];

  const serviceCards = [
    {
      id: 1,
      title: 'Colour Catalogue',
      description: 'Get Inspired And Paint Your Dream Home With Our 2500+ Shades',
      image: 'https://images.unsplash.com/photo-1525909002-1b05e0c869d8?w=500',
      buttonText: 'View Colour Catalogue',
      route: '/catalogue'
    },
    {
      id: 2,
      title: 'Paint Calculator',
      description: 'Calculate Paint Amount And Elevate Your Home With Confidence',
      image: 'https://plus.unsplash.com/premium_vector-1722610416591-9e8e6740d4e6?q=80&w=580&auto=format&fit=crop&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D',
      buttonText: 'Calculate Your Cost',
      route: '/calculator'
    },
    // {
    //   id: 3,
    //   title: 'Colour Visualizer',
    //   description: 'Choose From Specially Curated Patterns And Colours For Your Room',
    //   image: 'https://images.unsplash.com/photo-1452570053594-1b985d6ea890?w=500',
    //   buttonText: 'Visualize',
    //   route: '/catalogue'
    // },
    {
      id: 4,
      title: 'Products',
      description: 'Explore our comprehensive range of premium paint products and solutions',
      image: 'https://plus.unsplash.com/premium_photo-1681487367144-3ac7acfead6f?q=80&w=870&auto=format&fit=crop&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D',
      buttonText: 'View Products',
      route: '/products'
    },
    {
      id: 5,
      title: 'Special Products',
      description: 'Innovative solutions for unique painting requirements',
      image: 'https://plus.unsplash.com/premium_photo-1664298400987-527cf7a83e6e?w=500&auto=format&fit=crop&q=60&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxzZWFyY2h8MXx8ZW5hbWVsJTIwcGFpbnR8ZW58MHx8MHx8fDA%3D',
      buttonText: 'Special Products',
      route: '/products?category=5'
    }
  ];

  // Scroll animations - trigger every time element comes into view
  useEffect(() => {
    const handleScroll = () => {
      // Animate products header
      if (productsHeaderRef.current) {
        const rect = productsHeaderRef.current.getBoundingClientRect();
        const isInView = rect.top < window.innerHeight * 0.8 && rect.bottom > 0;
        
        if (isInView) {
          productsHeaderRef.current.classList.add('animate-in');
        } else {
          productsHeaderRef.current.classList.remove('animate-in');
        }
      }

      // Animate product cards
      productCardsRef.current.forEach((card, index) => {
        if (card) {
          const rect = card.getBoundingClientRect();
          const isInView = rect.top < window.innerHeight * 0.8 && rect.bottom > 0;
          
          if (isInView) {
            setTimeout(() => {
              card.classList.add('animate-in');
            }, index * 100);
          } else {
            card.classList.remove('animate-in');
          }
        }
      });

      // Animate feature cards
      featureCardsRef.current.forEach((card, index) => {
        if (card) {
          const rect = card.getBoundingClientRect();
          const isInView = rect.top < window.innerHeight * 0.8 && rect.bottom > 0;
          
          if (isInView) {
            setTimeout(() => {
              card.classList.add('animate-in');
            }, index * 120);
          } else {
            card.classList.remove('animate-in');
          }
        }
      });
    };

    window.addEventListener('scroll', handleScroll);
    handleScroll(); // Initial check
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleProductClick = (productName) => {
    const categoryId = categoryMapping[productName];
    if (categoryId) {
      navigate(`/products?category=${categoryId}`);
    } else {
      navigate('/products');
    }
  };

  const handleServiceCardClick = (route) => {
    navigate(route);
  };

  return (
    <div className="home">
      <head>
        <title>Aultra Paints - Experience Colour Like Never Before | Premium Paint Solutions</title>
        <meta name="description" content="Discover Aultra Paints premium quality interior, exterior, protective coatings, textures and special paint products. Transform your spaces with colors like never before." />
        <meta name="keywords" content="Aultra Paints, interior paints, exterior paints, protective coatings, textured paints, premium paints, paint solutions" />
        <meta name="viewport" content="width=device-width, initial-scale=1.0" />
        <meta property="og:title" content="Aultra Paints - Premium Paint Solutions" />
        <meta property="og:description" content="Experience colour like never before with Aultra Paints premium quality paint products and solutions." />
        <meta property="og:type" content="website" />
        <meta name="robots" content="index, follow" />
      </head>

      {/* Hero Video Section */}
      <section className="home__hero-video">
        <div className="home__video-container">
          <video
            ref={videoRef}
            className="home__video"
            autoPlay
            loop
            muted
            playsInline
          >
            <source src={heroVideo} type="video/mp4" />
            Your browser does not support the video tag.
          </video>
          <div className="home__video-overlay"></div>
        </div>
      </section>

      {/* Brand Message Section */}
      <section className="home__brand-message">
        <div className="home__container">
          <div className="home__brand-content">
            <h1 className="home__brand-title">
              Experience <span className="home__brand-color-text">colour</span> like never before
            </h1>
            <p className="home__brand-subtitle">
              — all at the touch of a button with...
            </p>
            <div className="home__brand-logo">
              <span className="home__logo-text">Aultra paints</span>
              <sup className="home__logo-reg">®</sup>
            </div>
            <div className="home__brand-tagline">
              <span className="home__group-text">Aultra group</span>
              <span className="home__since-text">(Since 2005)</span>
            </div>
          </div>
        </div>
      </section>

      {/* Products Section */}
      <section className="home__products">
        <div className="home__container">
          <div 
            className="home__products-header" 
            ref={productsHeaderRef}
          >
            <h2 className="home__products-title">Add Color To Your Home</h2>
            <p className="home__products-subtitle">
              One-stop essentials to keep every corner in check. 
            </p>
          </div>
          
          <div className="home__products-grid">
            {products.map((product, index) => (
              <div 
                key={product.id}
                className="home__product-card"
                onClick={() => handleProductClick(product.name)}
                ref={(el) => (productCardsRef.current[index] = el)}
              >
                <div className="home__product-image-wrapper">
                  <img 
                    src={product.image} 
                    alt={product.name}
                    className="home__product-img"
                  />
                  <div className="home__product-overlay"></div>
                </div>
                <div className="home__product-info">
                  <h3 className="home__product-name">{product.name}</h3>
                  <span className="home__product-arrow-icon">→</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
      
      <FeaturedProducts/>
      
      {/* Service Cards Section */}
      <section className="home__features">
        <div className="home__container">
          <div className="home__features-grid">
            {serviceCards.map((card, index) => (
              <div 
                key={card.id}
                className="home__feature-card"
                onClick={() => handleServiceCardClick(card.route)}
                ref={(el) => (featureCardsRef.current[index] = el)}
              >
                <div className="home__feature-image-wrapper">
                  <img 
                    src={card.image} 
                    alt={card.title}
                    className="home__feature-img"
                  />
                  <div className="home__feature-overlay"></div>
                </div>
                <div className="home__feature-content">
                  <h3 className="home__feature-title">{card.title}</h3>
                  <p className="home__feature-description">{card.description}</p>
                  <button className="home__feature-button">
                    {card.buttonText}
                    <span className="home__button-arrow">→</span>
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
      
      <Testimonials/>
    </div>
  );
};

export default Home;
